import MIMETypes from 'mime-types';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';

/**
 * Extensions treated as image (for mime type, preview, etc.).
 * Use isImageExtension(ext) for checks.
 */
export const IMAGE_EXTENSIONS = new Set<string>([
  ...EXTENSIONS.IMAGE,
]);

export function isImageExtension(extension: unknown): boolean {
  if (typeof extension !== 'string') {
    return false;
  }

  return IMAGE_EXTENSIONS.has(extension.toLowerCase());
}

export function isMimeTypeImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Returns lowercased extension (without dot) from filename.
 * Empty string if no extension.
 */
export function resolveExtensionFromFileName(fileName: string): string {
  const ext = fileName.split('.').pop();
  return (ext ?? '').toLowerCase();
}

/** The file name without its extension (as `resolveExtensionFromFileName` reads it); a name without one stays as it is. */
export function removeFileExtension(fileName: string): string {
  const extensionStart = fileName.lastIndexOf('.');
  return extensionStart > 0 ? fileName.slice(0, extensionStart) : fileName;
}

export function normalizeFileExtension(
  extension: string | undefined | null
): string {
  return (extension ?? '').replace(/^\./, '').toLowerCase();
}

export function resolveMimeTypeFromExtension(
  extension: string | undefined | null,
  fallbackMimeType: string = MIME_TYPES.OCTET_STREAM
): string {
  const normalizedExtension = normalizeFileExtension(extension);

  if (!normalizedExtension) {
    return fallbackMimeType;
  }

  const mimeTypeFromRegistry = MIMETypes?.types[normalizedExtension];
  return typeof mimeTypeFromRegistry === 'string' && mimeTypeFromRegistry.length > 0
    ? mimeTypeFromRegistry
    : fallbackMimeType;
}

export function resolveExtensionFromMimeType(
  mimeType: string | undefined | null,
  fallbackExtension = ''
): string {
  const normalizedMimeType = (mimeType ?? '').trim();
  if (!normalizedMimeType) {
    return fallbackExtension;
  }

  if (normalizedMimeType === 'image/pjpeg') {
    return EXTENSIONS.JPEG;
  }

  const extension = MIMETypes.extension(normalizedMimeType);
  return typeof extension === 'string' && extension.length > 0
    ? extension
    : fallbackExtension;
}

export function resolveMimeTypeFromFileName(
  fileName: string | undefined | null,
  fallbackMimeType: string = MIME_TYPES.OCTET_STREAM
): string {
  const extension = resolveExtensionFromFileName((fileName ?? '').trim());
  if (!extension) {
    return fallbackMimeType;
  }

  return resolveMimeTypeFromExtension(extension, fallbackMimeType);
}

/**
 * Maps extension to MIME type. Returns 'application/octet-stream' for unknown.
 */
export function mimeTypeFromExtension(extension: string): string {
  const ext = extension.toLowerCase();
  if (ext === EXTENSIONS.PDF) return MIME_TYPES.PDF;
  if (IMAGE_EXTENSIONS.has(ext)) {
    return ext === EXTENSIONS.JPG
      ? MIME_TYPES.IMAGE_JPEG
      : ext === EXTENSIONS.PNG
      ? MIME_TYPES.IMAGE_PNG
      : `image/${ext}`;
  }
  return MIME_TYPES.OCTET_STREAM;
}
